/**
 * Commodity Approvals & Modifications Page (KAN-307)
 *
 * Three-tab layout:
 *   1. Eligible Trades — Confirmed parent trades available for modification/cancellation
 *   2. Pending Requests — trades with pending modification requests (two-step flow)
 *   3. History — completed and rejected modifications/cancellations
 *
 * Phase 3 implements: Eligible Trades tab + single-step modify/cancel dialogs.
 * Phase 4 adds: Pending tab with two-step confirm/reject flow.
 * Phase 5 adds: History tab with detail view.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AG_GRID_LOCALE_ES } from '@ag-grid-community/locale';
import { AG_GRID_LOCALE_PT } from '@ag-grid-community/locale';
import { RefreshCw, AlertCircle, Inbox, CheckCircle, CheckCircle2, XCircle, X } from 'lucide-react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { useAuth } from '../../../components/auth/AuthContext';
import Toast from '../../../components/common/Toast';
import InlineMenu, { InlineMenuItem } from '../../../components/common/InlineMenu';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import { commodityModificationService } from '../../../services/api/commodityModificationService';
import CommodityModifyDialog from './CommodityModifyDialog';
import CommodityCancelDialog from './CommodityCancelDialog';
import CommodityPendingDetailPanel from './CommodityPendingDetailPanel';
import './CommodityModifications.css';

type TabId = 'eligible' | 'pending' | 'history';

const CommodityModifications: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();

  const agGridLocale = useMemo(() => {
    const lang = i18n.language?.substring(0, 2);
    if (lang === 'es') return AG_GRID_LOCALE_ES;
    if (lang === 'pt') return AG_GRID_LOCALE_PT;
    return undefined;
  }, [i18n.language]);

  const clientId = user?.organization?.id || user?.id;
  const canSelfApprove = user?.profile?.canSelfApproveModifications ?? false;

  // Tab state
  const [activeTab, setActiveTab] = useState<TabId>('eligible');

  // Data state
  const [eligibleTrades, setEligibleTrades] = useState<any[]>([]);
  const [pendingRequests, setPendingRequests] = useState<any[]>([]);
  const [historyEntries, setHistoryEntries] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Selection state
  const [selectedTrade, setSelectedTrade] = useState<any | null>(null);

  // Dialog state
  const [showModifyDialog, setShowModifyDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  // Context menu state (right-click on eligible trades grid)
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });
  const [contextMenuTrade, setContextMenuTrade] = useState<any | null>(null);

  // Grid refs
  const eligibleGridRef = useRef<AgGridReact>(null);
  const eligibleGridContainerRef = useRef<HTMLDivElement>(null);

  // Eligible trades date range filter (by Trade Date)
  const [eligibleDateFrom, setEligibleDateFrom] = useState('');
  const [eligibleDateTo, setEligibleDateTo] = useState('');

  // History detail panel
  const [selectedHistoryEntry, setSelectedHistoryEntry] = useState<any | null>(null);

  // Pending detail panel
  const [selectedPendingTrade, setSelectedPendingTrade] = useState<any | null>(null);

  // Success confirmation dialogs
  const [cancelSuccessInfo, setCancelSuccessInfo] = useState<{ tradeNumber: string; pending?: boolean } | null>(null);
  const [modifySuccessInfo, setModifySuccessInfo] = useState<{ tradeNumber: string; details: string; pending?: boolean } | null>(null);

  // Toast
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning' } | null>(null);

  // ── Data fetching ──

  const fetchEligibleTrades = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await commodityModificationService.getEligibleTrades(clientId);
      if (res.success) {
        setEligibleTrades(res.data || []);
      } else {
        setError(res.message || t('commodities.modifications.error'));
      }
    } catch (err: any) {
      setError(err.message || t('commodities.modifications.error'));
    } finally {
      setLoading(false);
    }
  }, [clientId, t]);

  const fetchPendingRequests = useCallback(async () => {
    if (!clientId) return;
    try {
      const res = await commodityModificationService.getPendingRequests(clientId);
      if (res.success) {
        setPendingRequests(res.data || []);
      }
    } catch {
      // Silently fail — pending tab is secondary
    }
  }, [clientId]);

  const fetchHistory = useCallback(async () => {
    if (!clientId) return;
    try {
      const res = await commodityModificationService.getHistory(clientId);
      if (res.success) {
        setHistoryEntries(res.data || []);
      }
    } catch {
      // Silently fail — history tab is secondary
    }
  }, [clientId]);

  const fetchAll = useCallback(() => {
    fetchEligibleTrades();
    fetchPendingRequests();
    fetchHistory();
  }, [fetchEligibleTrades, fetchPendingRequests, fetchHistory]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Right-click context menu for eligible trades grid
  useEffect(() => {
    const handleContextMenu = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const cellElement = target.closest('.ag-cell');
      if (!cellElement) return;
      if (!eligibleGridContainerRef.current?.contains(target)) return;

      e.preventDefault();
      e.stopPropagation();

      const rowElement = cellElement.closest('[role="row"]');
      if (rowElement && eligibleGridRef.current?.api) {
        const rowIndex = rowElement.getAttribute('row-index');
        if (rowIndex !== null) {
          const rowNode = eligibleGridRef.current.api.getDisplayedRowAtIndex(parseInt(rowIndex));
          if (rowNode) {
            rowNode.setSelected(true, true);
            setSelectedTrade(rowNode.data);
            setContextMenuTrade(rowNode.data);
            setContextMenuPosition({ x: e.clientX, y: e.clientY });
            setShowContextMenu(true);
          }
        }
      }
    };

    document.addEventListener('contextmenu', handleContextMenu);
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
    };
  }, []);

  // ── Formatting helpers ──

  const formatDate = (value: any): string => {
    if (!value) return '';
    const s = String(value).substring(0, 10);
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : s;
  };

  // DD/MM/YYYY HH:MM:SS for detail panel timestamps, converted to user timezone
  const userTimezone = user?.profile?.timezone || 'America/Santiago';
  const formatTimestamp = (value: any): string => {
    if (!value) return '—';
    const d = new Date(String(value));
    if (isNaN(d.getTime())) return String(value);
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: userTimezone,
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false,
    }).formatToParts(d);
    const get = (type: string) => parts.find(p => p.type === type)?.value || '';
    return `${get('day')}/${get('month')}/${get('year')} ${get('hour')}:${get('minute')}:${get('second')}`;
  };

  const formatNumber = useCallback((value: any): string => {
    if (value == null || value === '') return '—';
    const num = Number(value);
    if (isNaN(num)) return String(value);
    return num.toLocaleString(i18n.language, { minimumFractionDigits: 2, maximumFractionDigits: 4 });
  }, [i18n.language]);

  // Field label lookup (PascalCase key → i18n)
  const fieldLabel = useCallback((key: string) => {
    return t(`commodities.fields.${key.charAt(0).toLowerCase() + key.slice(1)}`, key);
  }, [t]);

  // Format a field value for display in the history detail panel
  const formatFieldValue = useCallback((key: string, value: any): string => {
    if (value == null || value === '') return '—';
    const s = String(value);
    // Date pattern
    const dm = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (dm) return `${dm[3]}/${dm[2]}/${dm[1]}`;
    // Numeric fields
    if (['Price', 'Lots', 'Quantity', 'Premium', 'StrikePrice'].includes(key)) {
      const num = Number(value);
      if (!isNaN(num)) return formatNumber(num);
    }
    return s;
  }, [formatNumber]);

  // ── Filtered eligible trades (by Trade Date range) ──

  const filteredEligibleTrades = useMemo(() => {
    if (!eligibleDateFrom && !eligibleDateTo) return eligibleTrades;
    const from = eligibleDateFrom ? new Date(eligibleDateFrom) : null;
    const to = eligibleDateTo ? new Date(eligibleDateTo) : null;
    return eligibleTrades.filter((trade) => {
      const dateStr = trade.TradeDate;
      if (!dateStr) return true;
      const d = new Date(String(dateStr).substring(0, 10));
      if (isNaN(d.getTime())) return true;
      if (from && d < from) return false;
      if (to && d > to) return false;
      return true;
    });
  }, [eligibleTrades, eligibleDateFrom, eligibleDateTo]);

  // ── Grid column definitions ──

  const eligibleColumns: ColDef[] = useMemo(() => [
    { field: 'TradeNumber', headerName: t('commodities.positions.grid.tradeNumber'), width: 110, sortable: true, filter: true },
    { field: 'ProductType', headerName: t('commodities.positions.grid.productType'), width: 100, filter: true },
    { field: 'UnderlyingName', headerName: t('commodities.positions.grid.underlyingName'), width: 160, filter: true },
    { field: 'Direction', headerName: t('commodities.positions.grid.direction'), width: 90 },
    { field: 'Lots', headerName: t('commodities.positions.grid.lots'), width: 80, type: 'numericColumn' },
    { field: 'Price', headerName: t('commodities.positions.grid.price'), width: 100, type: 'numericColumn',
      valueFormatter: (p: any) => p.value != null ? Number(p.value).toLocaleString(i18n.language, { minimumFractionDigits: 2, maximumFractionDigits: 4 }) : '' },
    { field: 'Currency', headerName: t('commodities.positions.grid.currency'), width: 70 },
    { field: 'Quantity', headerName: t('commodities.positions.grid.quantity'), width: 120, type: 'numericColumn',
      valueFormatter: (p: any) => p.value != null ? Number(p.value).toLocaleString(i18n.language, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '' },
    { field: 'TradeDate', headerName: t('commodities.positions.grid.tradeDate'), width: 110, sortable: true, valueFormatter: (p: any) => formatDate(p.value) },
    { field: 'MaturityDate', headerName: t('commodities.positions.grid.maturityDate'), width: 120, sortable: true, valueFormatter: (p: any) => formatDate(p.value) },
    { field: 'PaymentDate', headerName: t('commodities.positions.grid.paymentDate'), width: 110, sortable: true, valueFormatter: (p: any) => formatDate(p.value) },
    { field: 'CounterpartyName', headerName: t('commodities.positions.grid.counterpartyName'), width: 180, filter: true },
    { field: 'FixingReference', headerName: t('commodities.positions.grid.fixingReference'), width: 140, filter: true },
    { field: 'LegacyReference1', headerName: t('commodities.positions.grid.legacyReference1'), width: 140, filter: true },
    { field: 'Grouping1', headerName: t('commodities.positions.grid.grouping1'), width: 120, filter: true },
    { field: 'Notes', headerName: t('commodities.positions.grid.notes'), width: 150 },
  ], [t, i18n.language]);

  const pendingColumns: ColDef[] = useMemo(() => [
    { field: 'TradeNumber', headerName: t('commodities.positions.grid.tradeNumber'), width: 110 },
    { field: 'UnderlyingName', headerName: t('commodities.positions.grid.underlyingName'), width: 160 },
    { field: 'pendingRequest.action', headerName: t('commodities.modifications.pendingAction'), width: 100,
      valueFormatter: (p: any) => p.value === 'modify' ? t('commodities.modifications.actionModify') : t('commodities.modifications.actionCancel') },
    { field: 'pendingRequest.initiatedByName', headerName: t('commodities.modifications.pendingInitiatedBy'), width: 150 },
    { field: 'pendingRequest.initiatedAt', headerName: t('commodities.modifications.pendingDate'), width: 160, valueFormatter: (p: any) => formatDate(p.value) },
    { field: 'pendingRequest.comment', headerName: t('commodities.modifications.pendingComment'), width: 200 },
  ], [t]);

  // Filter child trades out of history
  const filteredHistoryEntries = useMemo(() => {
    return historyEntries.filter(t => !t.isInternalTrade);
  }, [historyEntries]);

  const historyColumns: ColDef[] = useMemo(() => [
    { field: 'TradeNumber', headerName: t('commodities.positions.grid.tradeNumber'), width: 110 },
    { field: 'ProductType', headerName: t('commodities.positions.grid.productType'), width: 100 },
    { field: 'UnderlyingName', headerName: t('commodities.positions.grid.underlyingName'), width: 160 },
    { field: 'LegacyReference1', headerName: t('commodities.positions.grid.legacyReference1'), width: 140 },
    { field: 'modificationRecord.action', headerName: t('commodities.modifications.historyAction'), width: 100,
      valueFormatter: (p: any) => p.value === 'modify' ? t('commodities.modifications.actionModify') : t('commodities.modifications.actionCancel') },
    { field: 'workflowState', headerName: t('commodities.modifications.workflowState'), width: 140,
      cellStyle: (p: any) => {
        if (p.value === 'Cancelled') return { color: '#ef4444' };
        if (p.value === 'Confirmed') return { color: '#22c55e' };
        return {};
      },
    },
    { field: 'modificationRecord.modifiedFields', headerName: t('commodities.modifications.historyChangedFields'), width: 200,
      valueFormatter: (p: any) => {
        if (!p.value) return '—';
        if (typeof p.value === 'object') {
          return Object.keys(p.value)
            .filter(k => k !== 'workflowHistory' && k !== 'hasInPlaceModification' && k !== 'hasPendingModification')
            .map(k => fieldLabel(k))
            .join(', ');
        }
        return String(p.value);
      },
    },
    { field: 'modificationRecord.resolvedAt', headerName: t('commodities.modifications.historyDate'), width: 160, sort: 'desc' as const, valueFormatter: (p: any) => formatTimestamp(p.value) },
  ], [t, fieldLabel, formatTimestamp]);

  // ── Handlers ──

  const handleModifyClick = useCallback(() => {
    if (selectedTrade) setShowModifyDialog(true);
  }, [selectedTrade]);

  const handleCancelClick = useCallback(() => {
    if (selectedTrade) setShowCancelDialog(true);
  }, [selectedTrade]);

  const handleModifySuccess = useCallback((result: any) => {
    setShowModifyDialog(false);
    setSelectedTrade(null);
    if (result.status === 'pending') {
      setModifySuccessInfo({ tradeNumber: result.tradeNumber || '', details: t('commodities.modifications.requestSubmittedDetail'), pending: true });
    } else {
      const details: string[] = [];
      if (result.quantityRecalculated) details.push(t('commodities.modifications.quantityRecalculated'));
      if (result.childrenUpdated > 0) details.push(t('commodities.modifications.childrenUpdated', { count: result.childrenUpdated }));
      setModifySuccessInfo({ tradeNumber: result.tradeNumber || '', details: details.join('. ') });
    }
    fetchAll();
  }, [t, fetchAll]);

  const handleCancelSuccess = useCallback((result: any) => {
    setShowCancelDialog(false);
    setSelectedTrade(null);
    if (result.status === 'pending') {
      setCancelSuccessInfo({ tradeNumber: result.tradeNumber || '', pending: true });
    } else {
      setCancelSuccessInfo({ tradeNumber: result.tradeNumber || '' });
    }
    fetchAll();
  }, [t, fetchAll]);

  // ── Pending confirm/reject handlers ──

  const handlePendingConfirmSuccess = useCallback((result: any) => {
    setSelectedPendingTrade(null);
    const action = result.action || 'modify';
    if (action === 'cancel') {
      setCancelSuccessInfo({ tradeNumber: result.tradeNumber || '' });
    } else {
      const details: string[] = [];
      if (result.quantityRecalculated) details.push(t('commodities.modifications.quantityRecalculated'));
      if (result.childrenUpdated > 0) details.push(t('commodities.modifications.childrenUpdated', { count: result.childrenUpdated }));
      setModifySuccessInfo({ tradeNumber: result.tradeNumber || '', details: details.join('. ') });
    }
    fetchAll();
  }, [t, fetchAll]);

  const handlePendingRejectSuccess = useCallback(() => {
    setSelectedPendingTrade(null);
    setToast({ message: t('commodities.modifications.rejectSuccess'), type: 'success' });
    fetchAll();
  }, [t, fetchAll]);

  // Derive whether current user can confirm the selected pending request
  const canConfirmPending = useMemo(() => {
    if (!selectedPendingTrade) return false;
    const initiatorUid = selectedPendingTrade.pendingRequest?.initiatedBy;
    const currentUid = user?.uid || user?.id;
    // Can confirm if: self-approve allowed OR current user is not the initiator
    if (canSelfApprove) return true;
    if (initiatorUid && currentUid && initiatorUid === currentUid) return false;
    return true;
  }, [selectedPendingTrade, user, canSelfApprove]);

  // ── Render helpers ──

  const renderTabContent = () => {
    if (loading) {
      return (
        <div className="commodity-modifications-loading">
          <div className="spinner" style={{ width: '20px', height: '20px', borderWidth: '2px' }} />
          {t('commodities.modifications.loading')}
        </div>
      );
    }

    if (error) {
      return (
        <div className="commodity-modifications-error">
          <AlertCircle size={20} />
          {error}
        </div>
      );
    }

    if (activeTab === 'eligible') {
      if (eligibleTrades.length === 0) {
        return (
          <div className="commodity-modifications-empty">
            <Inbox size={40} />
            <span>{t('commodities.modifications.noEligibleTrades')}</span>
          </div>
        );
      }
      return (
        <>
          {/* Action bar above the grid (matching FX pattern) */}
          <div className="commodity-modifications-action-bar">
            <div className="commodity-modifications-action-bar-left">
              {selectedTrade
                ? t('commodities.modifications.selectedTrade', { number: selectedTrade.TradeNumber })
                : t('commodities.modifications.selectTrade')
              }
            </div>
            <div className="commodity-modifications-action-bar-right">
              <DateRangeFilter
                dateFrom={eligibleDateFrom}
                dateTo={eligibleDateTo}
                onApply={(from, to) => {
                  setEligibleDateFrom(from);
                  setEligibleDateTo(to);
                  setSelectedTrade(null);
                }}
                label={t('commodities.positions.grid.tradeDate')}
                disabled={loading}
              />
              <button
                className="commodity-modifications-action-btn"
                disabled={!selectedTrade}
                onClick={handleModifyClick}
              >
                {t('commodities.modifications.modify')}
              </button>
              <button
                className="commodity-modifications-action-btn danger"
                disabled={!selectedTrade}
                onClick={handleCancelClick}
              >
                {t('commodities.modifications.cancelTrade')}
              </button>
            </div>
          </div>
          <div ref={eligibleGridContainerRef} className="commodity-modifications-grid-container ag-theme-alpine-dark">
            <AgGridReact
              ref={eligibleGridRef}
              rowData={filteredEligibleTrades}
              columnDefs={eligibleColumns}
              localeText={agGridLocale}
              rowSelection="single"
              onRowClicked={(event) => setSelectedTrade(event.data)}
              getRowId={(params) => params.data.id || params.data.TradeNumber}
              defaultColDef={{
                resizable: true,
                sortable: true,
                minWidth: 60,
              }}
              suppressContextMenu={true}
              pagination={true}
              paginationPageSize={50}
              animateRows={true}
              suppressCellFocus={true}
            />
          </div>
        </>
      );
    }

    if (activeTab === 'pending') {
      if (pendingRequests.length === 0) {
        return (
          <div className="commodity-modifications-empty">
            <Inbox size={40} />
            <span>{t('commodities.modifications.noPendingRequests')}</span>
          </div>
        );
      }
      return (
        <div className="commodity-mod-history-layout">
          <div className="commodity-modifications-grid-container ag-theme-alpine-dark">
            <AgGridReact
              rowData={pendingRequests}
              columnDefs={pendingColumns}
              localeText={agGridLocale}
              rowSelection="single"
              getRowId={(params) => params.data.id || params.data.TradeNumber}
              onRowClicked={(event) => setSelectedPendingTrade(event.data)}
              defaultColDef={{
                resizable: true,
                sortable: true,
                filter: true,
              }}
              onFirstDataRendered={(e) => e.api.autoSizeAllColumns()}
              animateRows={true}
              suppressCellFocus={true}
            />
          </div>

          {selectedPendingTrade && (
            <CommodityPendingDetailPanel
              trade={selectedPendingTrade}
              clientId={clientId!}
              canConfirm={canConfirmPending}
              userTimezone={userTimezone}
              onConfirmSuccess={handlePendingConfirmSuccess}
              onRejectSuccess={handlePendingRejectSuccess}
              onClose={() => setSelectedPendingTrade(null)}
            />
          )}
        </div>
      );
    }

    if (activeTab === 'history') {
      if (filteredHistoryEntries.length === 0) {
        return (
          <div className="commodity-modifications-empty">
            <Inbox size={40} />
            <span>{t('commodities.modifications.noHistory')}</span>
          </div>
        );
      }

      const rec = selectedHistoryEntry?.modificationRecord;
      const isModify = rec?.action === 'modify';
      const isCancellation = rec?.action === 'cancel';
      const modifiedFields = rec?.modifiedFields || {};
      const originalFields = rec?.originalFieldValues || {};
      const changedKeys = isModify ? Object.keys(modifiedFields).filter(k => k !== 'workflowHistory' && k !== 'hasInPlaceModification' && k !== 'hasPendingModification') : [];

      return (
        <div className="commodity-mod-history-layout">
          <div className="commodity-modifications-grid-container ag-theme-alpine-dark">
            <AgGridReact
              rowData={filteredHistoryEntries}
              columnDefs={historyColumns}
              localeText={agGridLocale}
              rowSelection="single"
              getRowId={(params) => params.data.id || params.data.TradeNumber}
              onRowClicked={(event) => setSelectedHistoryEntry(event.data)}
              defaultColDef={{
                resizable: true,
                sortable: true,
                filter: true,
              }}
              onFirstDataRendered={(e) => e.api.autoSizeAllColumns()}
              animateRows={true}
              suppressCellFocus={true}
              suppressContextMenu={true}
            />
          </div>

          {selectedHistoryEntry && rec && (
            <div className="commodity-mod-detail-panel">
              {/* Header */}
              <div className="commodity-mod-detail-header">
                <div>
                  <div className="commodity-mod-detail-trade-num">
                    {t('commodities.modifications.detailTitle', { tradeNumber: selectedHistoryEntry.TradeNumber })}
                  </div>
                  <div className="commodity-mod-detail-status">
                    {isModify ? (
                      <span className="commodity-mod-detail-badge confirmed">
                        <CheckCircle2 size={12} />
                        {t('commodities.modifications.statusConfirmed')}
                      </span>
                    ) : (
                      <span className="commodity-mod-detail-badge cancelled">
                        <XCircle size={12} />
                        {t('commodities.modifications.actionCancel')}
                      </span>
                    )}
                  </div>
                </div>
                <button className="commodity-mod-detail-close" onClick={() => setSelectedHistoryEntry(null)}>
                  <X size={16} />
                </button>
              </div>

              {/* Trade summary */}
              <table className="commodity-mod-detail-table">
                <tbody>
                  <tr>
                    <td className="detail-label">{fieldLabel('ProductType')}</td>
                    <td colSpan={2}>{selectedHistoryEntry.ProductType || '—'}</td>
                  </tr>
                  <tr>
                    <td className="detail-label">{fieldLabel('UnderlyingName')}</td>
                    <td colSpan={2}>{selectedHistoryEntry.UnderlyingName || '—'}</td>
                  </tr>
                  {selectedHistoryEntry.LegacyReference1 && (
                    <tr>
                      <td className="detail-label">{t('commodities.positions.grid.legacyReference1')}</td>
                      <td colSpan={2}>{selectedHistoryEntry.LegacyReference1}</td>
                    </tr>
                  )}
                  <tr>
                    <td className="detail-label">{fieldLabel('CounterpartyName')}</td>
                    <td colSpan={2}>{selectedHistoryEntry.CounterpartyName || '—'}</td>
                  </tr>
                  <tr>
                    <td className="detail-label">{t('commodities.positions.grid.direction')}</td>
                    <td colSpan={2}>{selectedHistoryEntry.Direction || '—'}</td>
                  </tr>
                  <tr>
                    <td className="detail-label">{fieldLabel('Lots')}</td>
                    <td colSpan={2}>{selectedHistoryEntry.Lots ?? '—'}</td>
                  </tr>
                  <tr>
                    <td className="detail-label">{fieldLabel('Price')}</td>
                    <td colSpan={2}>{formatNumber(selectedHistoryEntry.Price)}</td>
                  </tr>
                  <tr>
                    <td className="detail-label">{fieldLabel('Quantity')}</td>
                    <td colSpan={2}>{formatNumber(selectedHistoryEntry.Quantity)}</td>
                  </tr>
                </tbody>
              </table>

              <div className="commodity-mod-detail-divider" />

              {/* Workflow info */}
              <table className="commodity-mod-detail-table">
                <tbody>
                  <tr>
                    <td className="detail-label">{t('commodities.modifications.workflowState')}</td>
                    <td colSpan={2}>{selectedHistoryEntry.workflowState || '—'}</td>
                  </tr>
                  {rec.initiatedByName && (
                    <tr>
                      <td className="detail-label">{t('commodities.modifications.detailInitiatedBy')}</td>
                      <td colSpan={2}>{rec.initiatedByName}{rec.initiatedAt ? ` — ${formatTimestamp(rec.initiatedAt)}` : ''}</td>
                    </tr>
                  )}
                  {rec.resolvedByName && (
                    <tr>
                      <td className="detail-label">{t('commodities.modifications.detailApprovedBy')}</td>
                      <td colSpan={2}>{rec.resolvedByName}{rec.resolvedAt ? ` — ${formatTimestamp(rec.resolvedAt)}` : ''}</td>
                    </tr>
                  )}
                  {rec.comment && (
                    <tr>
                      <td className="detail-label">{t('commodities.modifications.commentLabel')}</td>
                      <td colSpan={2} className="detail-comment">{rec.comment}</td>
                    </tr>
                  )}
                </tbody>
              </table>

              {/* Changed fields (modifications only) */}
              {isModify && changedKeys.length > 0 && (
                <>
                  <div className="commodity-mod-detail-divider" />
                  <div className="commodity-mod-detail-section-label">
                    {t('commodities.modifications.detailChangedFields')}
                  </div>
                  <table className="commodity-mod-detail-table commodity-mod-detail-changes">
                    <thead>
                      <tr>
                        <th>{t('commodities.modifications.detailField')}</th>
                        <th>{t('commodities.modifications.currentValue')}</th>
                        <th>{t('commodities.modifications.newValue')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {changedKeys.map((key) => (
                        <tr key={key}>
                          <td className="detail-label">{fieldLabel(key)}</td>
                          <td className="detail-from">{formatFieldValue(key, originalFields[key])}</td>
                          <td className="detail-to">{formatFieldValue(key, modifiedFields[key])}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}

              {/* Cancellation reason */}
              {isCancellation && rec.cancellationReason && (
                <>
                  <div className="commodity-mod-detail-divider" />
                  <div className="commodity-mod-detail-section-label">
                    {t('commodities.modifications.detailReason')}
                  </div>
                  <div className="commodity-mod-detail-reason">{rec.cancellationReason}</div>
                </>
              )}
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  return (
    <div className="commodity-modifications-page">
      {/* Header */}
      <div className="commodity-modifications-header">
        <div className="commodity-modifications-header-left">
          <h1 className="commodity-modifications-title">
            {t('commodities.modifications.title')}
          </h1>
        </div>
        <div className="commodity-modifications-header-right">
          <button
            className="commodity-modifications-refresh-btn"
            onClick={fetchAll}
            disabled={loading}
          >
            <RefreshCw size={14} />
            {t('commodities.modifications.refresh')}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="commodity-modifications-tabs">
        <button
          className={`commodity-modifications-tab ${activeTab === 'eligible' ? 'active' : ''}`}
          onClick={() => { setActiveTab('eligible'); setSelectedHistoryEntry(null); setSelectedPendingTrade(null); }}
        >
          {t('commodities.modifications.tabs.eligible')}
        </button>
        <button
          className={`commodity-modifications-tab ${activeTab === 'pending' ? 'active' : ''}`}
          onClick={() => { setActiveTab('pending'); setSelectedHistoryEntry(null); }}
        >
          {t('commodities.modifications.tabs.pending')}
          {pendingRequests.length > 0 && (
            <span className="commodity-modifications-tab-badge">
              {pendingRequests.length}
            </span>
          )}
        </button>
        <button
          className={`commodity-modifications-tab ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => { setActiveTab('history'); setSelectedPendingTrade(null); }}
        >
          {t('commodities.modifications.tabs.history')}
        </button>
      </div>

      {/* Content */}
      <div className="commodity-modifications-content">
        {renderTabContent()}
      </div>

      {/* Right-click context menu (eligible trades grid) */}
      {showContextMenu && contextMenuTrade && (
        <InlineMenu
          position={contextMenuPosition}
          onClose={() => setShowContextMenu(false)}
          items={[
            {
              label: t('commodities.modifications.modify'),
              action: () => {
                setShowContextMenu(false);
                setShowModifyDialog(true);
              },
            },
            {
              label: t('commodities.modifications.cancelTrade'),
              action: () => {
                setShowContextMenu(false);
                setShowCancelDialog(true);
              },
              className: 'danger',
            },
          ] as InlineMenuItem[]}
        />
      )}

      {/* Dialogs */}
      {showModifyDialog && selectedTrade && (
        <CommodityModifyDialog
          trade={selectedTrade}
          clientId={clientId!}
          canSelfApprove={canSelfApprove}
          onSuccess={handleModifySuccess}
          onClose={() => setShowModifyDialog(false)}
        />
      )}
      {showCancelDialog && selectedTrade && (
        <CommodityCancelDialog
          trade={selectedTrade}
          clientId={clientId!}
          canSelfApprove={canSelfApprove}
          onSuccess={handleCancelSuccess}
          onClose={() => setShowCancelDialog(false)}
        />
      )}

      {/* Modification success dialog */}
      {modifySuccessInfo && (
        <div className="commodity-mod-dialog-overlay" onClick={() => setModifySuccessInfo(null)}>
          <div className="commodity-mod-dialog" style={{ maxWidth: '420px' }} onClick={(e) => e.stopPropagation()}>
            <div className="commodity-mod-dialog-body" style={{ textAlign: 'center', padding: '2rem' }}>
              <CheckCircle size={48} style={{ color: modifySuccessInfo.pending ? '#f59e0b' : '#22c55e', marginBottom: '1rem' }} />
              <h3 style={{ color: 'var(--text-primary, #e2e8f0)', margin: '0 0 0.5rem 0', fontSize: '1.1rem' }}>
                {modifySuccessInfo.pending
                  ? t('commodities.modifications.requestSubmitted')
                  : t('commodities.modifications.modificationComplete')}
              </h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', margin: 0 }}>
                {modifySuccessInfo.pending
                  ? t('commodities.modifications.requestSubmittedDetail', { tradeNumber: modifySuccessInfo.tradeNumber })
                  : t('commodities.modifications.modifySuccess', { tradeNumber: modifySuccessInfo.tradeNumber })}
              </p>
              {!modifySuccessInfo.pending && modifySuccessInfo.details && (
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', margin: '0.5rem 0 0 0' }}>
                  {modifySuccessInfo.details}
                </p>
              )}
            </div>
            <div className="commodity-mod-dialog-footer" style={{ justifyContent: 'center' }}>
              <button className="btn-primary" onClick={() => setModifySuccessInfo(null)}>
                {t('common.ok')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancellation success dialog */}
      {cancelSuccessInfo && (
        <div className="commodity-mod-dialog-overlay" onClick={() => setCancelSuccessInfo(null)}>
          <div className="commodity-mod-dialog" style={{ maxWidth: '420px' }} onClick={(e) => e.stopPropagation()}>
            <div className="commodity-mod-dialog-body" style={{ textAlign: 'center', padding: '2rem' }}>
              <CheckCircle size={48} style={{ color: cancelSuccessInfo.pending ? '#f59e0b' : '#22c55e', marginBottom: '1rem' }} />
              <h3 style={{ color: 'var(--text-primary, #e2e8f0)', margin: '0 0 0.5rem 0', fontSize: '1.1rem' }}>
                {cancelSuccessInfo.pending
                  ? t('commodities.modifications.requestSubmitted')
                  : t('commodities.modifications.cancellationComplete')}
              </h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', margin: 0 }}>
                {cancelSuccessInfo.pending
                  ? t('commodities.modifications.requestSubmittedDetail', { tradeNumber: cancelSuccessInfo.tradeNumber })
                  : t('commodities.modifications.cancelSuccess', { tradeNumber: cancelSuccessInfo.tradeNumber })}
              </p>
            </div>
            <div className="commodity-mod-dialog-footer" style={{ justifyContent: 'center' }}>
              <button className="btn-primary" onClick={() => setCancelSuccessInfo(null)}>
                {t('common.ok')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default CommodityModifications;
